<template>
  <div class="order-status-table orders" ref="orderStatusTable">
    <e-table
      ref="table"
      :name="tableName"
      :items="items"
      :rowClass="rowClass"
      :cellClass="cellClass"
      :coreColumnDefs="columnDefs"
      :columnEvent="columnEvent"
      :defaultSort="{}"
      :bufferScale="3"
      :scrollingSpeed="0.3"
      :getHeight="getHeight"
      @loadMore="$emit('loadMore')"
      @contextmenu="contextMenu.trigger"
    />

    <e-contextmenu
      ref="contextmenu"
      :target="contextMenu.target"
      :options="contextMenu.options"
      @handle="contextMenu.handle"
    />
  </div>
</template>
<script>
import api from "@/default/utils/api";
import "@/default/pages/containers/order_status/cells/index.js";
import "@/default/pages/containers/shared/shared.js";
export default {
  title: "ContainerOrderStatusTable",
  mixins: [api],
  props: {
    items: {
      type: Array,
      required: true,
    },
    status: {
      type: String,
      default: null,
    },
  },
  data() {
    return {
      contextMenu: {
        target: "",
        options: [
          {
            type: "SEPARATOR",
          },
          {
            label: "View Order",
            type: "view-order",
          },
          {
            label: "Edit Order",
            type: "edit-order",
          },
        ],
        trigger: (row, rowIndex, event) => {
          this.$refs.contextmenu.trigger(row, rowIndex, event);
        },
        handle: (type, row) => {
          let options = {};
          switch (type) {
            case "view-order":
              options = this.viewOrderOptions(row.id);
              Dialog.open({ width: "900px", clickToClose: true, ...options });
              break;
            case "edit-order":
              options = this.editOrderOptions(row.id);
              Dialog.open({ width: "900px", clickToClose: true, ...options });
              break;
          }
        },
        initTarget: () => {
          if (this.$refs.orderStatusTable)
            this.contextMenu.target = this.$refs.orderStatusTable;
        },
      },
    };
  },
  mounted() {
    this.contextMenu.initTarget();
  },
  updated() {
    this.contextMenu.initTarget();
  },
  watch: {
    status(nv, ov) {
      if (nv) this.initCachedColumns();
    },
  },
  computed: {
    inYard() {
      return this.status == "yard";
    },
    rowHeight() {
      return this.inYard ? 50 : 75;
    },
    tableName() {
      return `order-status-${this.status}`;
    },
    coreColumnDefs() {
      return [
        {
          title: "ID",
          field: "id",
          key: "id",
          width: 60,
          fixed: "left",
          classes: "cell-style",
          component: "order-status-id",
        },
        {
          title: "Action",
          key: "action",
          width: 72,
          minWidth: 72,
          fixed: "left",
          classes: "cell-style",
          component: "order-status-action",
        },
        {
          title: "Hub",
          field: "hub.name",
          key: "hub",
          width: this.inYard ? 125 : 72,
          component: "ve-table-column",
        },
        {
          title: "Type",
          field: "abbrev_type",
          key: "type",
          width: 60,
          component: "ve-table-column",
        },
        {
          title: "Customer",
          field: "customer.name",
          key: "customer",
          width: 150,
          only: [
            "default",
            "on_site",
            "empty_return",
            "outbound_ready",
            "in_transit",
          ],
          component: "ve-table-column",
        },
        {
          title: "Ssline",
          field: "ssline.name",
          key: "ssline",
          width: this.inYard ? 125 : 100,
          component: "order-status-ssline",
        },
        {
          title: "Warehouse",
          field: "clients.name",
          key: "warehouse",
          width: 150,
          only: ["default", "on_site"],
          component: "order-status-warehouse-name",
        },
        {
          title: "City",
          field: "clients.city",
          key: "warehouse_city",
          width: 80,
          only: ["default", "on_site"],
          component: "order-status-warehouse-city",
        },
        {
          title: "State",
          field: "clients.state",
          key: "warehouse_state",
          width: 60,
          only: ["default", "on_site"],
          component: "order-status-warehouse-state",
        },
        {
          title: "CONT.#",
          field: "container_no",
          key: "container_no",
          width: 112,
          only: [
            "default",
            "on_site",
            "empty_return",
            "outbound_ready",
            "in_transit",
          ],
          component: "order-status-cont-no",
        },
        {
          title: "Size/Type",
          field: "ctype",
          key: "ctype",
          width: 100,
          only: [
            "default",
            "on_site",
            "empty_return",
            "outbound_ready",
            "in_transit",
          ],
          component: "ve-table-column",
        },
        {
          title: "Pickup#",
          field: "pickup_no",
          key: "pickup_no",
          width: 80,
          only: ["default", "on_site"],
          component: "ve-table-column",
        },
        {
          title: "Chassis#",
          field: "chassis_no",
          key: "chassis_no",
          width: 100,
          only: ["default", "on_site", "empty_return"],
          component: "order-status-chassis-no",
        },
        {
          title: "Chassis Type",
          field: "chassis_type",
          key: "chassis_type",
          width: 100,
          only: ["default", "on_site", "empty_return"],
          component: "ve-table-column",
        },
        {
          title: "Terminals",
          field: "terminals",
          key: "terminals",
          width: 125,
          only: ["default", "on_site"],
          component: "order-status-terminals",
        },
        {
          title: "Yards",
          field: "yards",
          key: "yards",
          width: 135,
          only: ["default", "on_site"],
          component: "order-status-yards",
        },
        {
          title: "Appt. Time",
          field: "appt_date",
          key: "appt",
          width: 135,
          minWidth: 135,
          only: ["default", "on_site", "yard"],
          component: "appt-time",
        },
        {
          title: "Drop Date",
          field: "drop_date",
          key: "drop_date",
          width: 90,
          minWidth: 90,
          only: ["on_site"],
          component: "ve-table-column",
        },
        {
          title: "Hook Date",
          field: "hook_date",
          key: "hook_date",
          width: 90,
          minWidth: 90,
          only: ["on_site"],
          component: "ve-table-column",
        },
        {
          title: "Notify Ready",
          field: "notified_at",
          key: "notified_at",
          width: 150,
          only: [
            "default",
            "on_site",
            "empty_return",
            "outbound_ready",
            "in_transit",
          ],
          component: "order-status-notified-at",
        },
        {
          title: "Disch. Date",
          field: "discharged_date",
          key: "discharged_date",
          width: 100,
          only: ["default", "on_site"],
          component: "ve-table-column",
        },
        {
          title: "Line LFD",
          field: "line_lfd",
          key: "line_lfd",
          width: 100,
          only: ["default", "on_site", "empty_return"],
          component: "ve-table-column",
        },
        {
          title: "Term. ETA",
          field: "eta",
          key: "eta",
          width: 100,
          only: ["default", "on_site"],
          component: "order-status-eta",
        },
        {
          title: "Term. LFD",
          field: "lfd",
          key: "lfd",
          width: 100,
          only: ["default", "on_site", "empty_return"],
          component: "order-status-lfd",
        },
        {
          title: "Vendor",
          field: "vendor",
          key: "vendor",
          width: 80,
          only: ["default", "on_site", "empty_return"],
          component: "ve-table-column",
        },
        {
          title: "PD LFD",
          field: "per_diem_lfd",
          key: "per_diem_lfd",
          width: 100,
          classes: "cell-style tight",
          component: "order-status-per-diem-lfd",
        },
        {
          title: "ERD",
          field: "erd",
          key: "erd",
          width: 100,
          only: ["default", "on_site"],
          component: "order-status-erd",
        },
        {
          title: "COD",
          field: "cod",
          key: "cod",
          width: 100,
          only: ["default", "on_site", "outbound_ready", "in_transit"],
          component: "order-status-cod",
        },
        {
          title: "CONT.#",
          field: "operation.container_no",
          key: "yard_container_no",
          width: 112,
          only: ["yard"],
          component: "order-status-yard-container",
        },
        {
          title: "Size/Type",
          field: "operation.ctype",
          key: "yard_ctype",
          width: 80,
          only: ["yard"],
          component: "ve-table-column",
        },
        {
          title: "Chassis#",
          field: "operation.chassis_no",
          key: "yard_chassis_no",
          width: 100,
          only: ["yard"],
          component: "order-status-yard-chassis",
        },
        {
          title: "Chassis Type",
          field: "operation.chassis_type",
          key: "yard_chassis_type",
          width: 100,
          only: ["yard"],
          component: "ve-table-column",
        },
        {
          title: "Empty Appt.",
          field: "empty_appt.timestamp",
          key: "empty_appt",
          identity: "empty_appt",
          width: 100,
          minWidth: 100,
          only: ["empty_return"],
          component: "order-status-route-appt",
        },
        {
          title: "Current Action",
          field: "operation.action",
          key: "current_action",
          width: 150,
          only: ["empty_return", "outbound_ready", "in_transit", "yard"],
          component: "ve-table-column",
        },
        {
          title: "Current Location",
          field: "operation.location",
          key: "current_location",
          width: 150,
          only: ["empty_return", "outbound_ready", "in_transit", "yard"],
          component: "ve-table-column",
        },
        {
          title: "Next Action",
          field: "operation.next_action",
          key: "next_action",
          width: 150,
          only: ["outbound_ready", "in_transit", "yard"],
          component: "ve-table-column",
        },
        {
          title: "Next Location",
          field: "operation.next_location",
          key: "next_location",
          width: 150,
          only: ["empty_return", "outbound_ready", "in_transit", "yard"],
          component: "ve-table-column",
        },
        {
          title: "Driver",
          field: "operation.trucker.name",
          key: "current_trucker",
          width: 150,
          only: ["in_transit"],
          component: "ve-table-column",
        },
        {
          title: "Return Instructions",
          field: "operation.return.instructions",
          key: "yard_return_instructions",
          width: 150,
          only: ["yard"],
          component: "order-status-return-instructions",
        },
        {
          title: "Last Operated",
          field: "operation.operated_at",
          key: "yard_date",
          width: 125,
          only: ["outbound_ready", "in_transit", "yard"],
          component: "ve-table-column",
        },
        {
          title: "Days",
          field: "operation.days",
          key: "yard_days",
          width: 80,
          only: ["outbound_ready", "yard"],
          component: "order-status-yard-days",
        },
        {
          title: "Status",
          field: "operation.status",
          key: "yard_status",
          width: 80,
          only: ["yard"],
          component: "order-status-yard-status",
        },
        {
          title: "Truckers",
          field: "dfactor",
          key: "truckers",
          width: 250,
          only: ["default", "on_site", "empty_return"],
          component: "order-status-truckers",
        },
        {
          title: "Dispatch",
          field: "dispatch_comment",
          key: "dispatch_comment",
          width: 150,
          component: "order-status-dispatch-comment",
        },
        {
          title: "Tasks",
          key: "tasks",
          width: 150,
          component: "order-status-tasks",
        },
      ];
    },
    columnOnly() {
      switch (this.status) {
        case "yard":
          return "yard";
        case "empty_return":
          return "empty_return";
        case "outbound_ready":
          return "outbound_ready";
        case "on_site":
          return "on_site";
        case "in_transit":
          return "in_transit";
        default:
          return "default";
      }
    },
    columnDefs() {
      const only = this.columnOnly;
      const columns = this.coreColumnDefs.filter(
        (o) => o.only == null || o.only.includes(only),
      );
      return columns;
    },
  },
  methods: {
    initCachedColumns() {
      const tableName = `order-status-${this.status}`;
      this.$actions.renameTableColumn(tableName, "ETA", "Term. ETA");
      this.$actions.renameTableColumn(tableName, "LFD", "Term. LFD");
      this.$actions.renameTableColumn(tableName, "Notified", "Notify Ready");
      const columnNames = this.columnDefs.map((col) => col.title);
      this.initTableColumns(tableName, columnNames);
      this.$emit("initColumnOptions");
    },
    getHeight() {
      const main = this.$el && this.$el.closest(".order-status");
      const widget = main && main.querySelector(".stats-filter");
      if (!main || !widget || main.offsetHeight <= 0) return;
      const height = main.offsetHeight - widget.offsetHeight - 4;
      return height > 0 ? height : undefined;
    },
    rowClass({ row }) {
      let rowColor = `row-${row.color}`;
      return { [rowColor]: true };
    },
    cellClass({ row, column }) {
      const portOnly = ["discharged_date", "line_lfd"];
      const railOnly = ["pickup_no", "eta"];
      const rail = row.drayage_mode == "rail";
      const port = row.drayage_mode == "port";
      const unused =
        (rail && portOnly.includes(column.field)) ||
        (port && railOnly.includes(column.field)) ||
        (row.live_load && column.field == "notified_at");
      if (unused) return "cell-style cell-unused";
    },
    columnEvent(fn, ...args) {
      this[fn](...args);
    },
    async autoSave(item, column, value) {
      try {
        let params = { type: item.type, column, value };
        await this.$http.put(`/api/v1/attrs/${item.id}`, params);
        this.$message({
          message: "It was changed successfully",
          type: "success",
        });
      } catch (ex) {
        this.$message({ message: ex.response.data.errors, type: "error" });
      }
    },
  },
};
</script>
<style lang="scss">
.order-status-table {
  .route-view {
    display: flex;
    justify-content: flex-start;
    font-size: 11px;
    background: #f0f0f0;

    &:not(:first-child) {
      border-top: 3px solid #fff;
    }

    .ops {
      text-align: left;
    }

    .name {
      text-align: right;
      font-style: italic;
    }

    .name,
    .ops {
      width: 125px;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .name:empty:before {
      color: red;
      content: "No Trucker";
    }
  }
}
</style>
